// src/games/MenteurMenteur.tsx
import { useState, useRef } from "react";
import OpenAI from "openai";
import { useApiKey } from "../ApiKeyContext";
import "./MenteurMenteur.css";
import GameRulesCard from "./GameRulesCard";
import "./GameRulesCard.css";
import menteurLogo from "../assets/logo-menteur.png";

type GameStep = "rules" | "register" | "reading" | "voting" | "reveal" | "end";

interface RoundData {
  theme: string;
  affirmations: string[];
  correctIndex: number;
}

export default function MenteurMenteur({ onQuit }: { onQuit: () => void }) {
  const apiKey = useApiKey();
  const openai = new OpenAI({ apiKey, dangerouslyAllowBrowser: true });
  const assistantId = "asst_QhNr6kEHczoSnnpMcxhPte3F";
  const threadId = useRef<string | null>(null);

  const [step, setStep] = useState<GameStep>("rules");
  const [players, setPlayers] = useState<string[]>([]);
  const [scores, setScores] = useState<Record<string, number>>({});
  const [nameInput, setNameInput] = useState("");
  const [round, setRound] = useState(1);
  const [roundData, setRoundData] = useState<RoundData | null>(null);
  const [currentReader, setCurrentReader] = useState(0);
  const [votes, setVotes] = useState<Record<string, number | null>>({});
  const [loading, setLoading] = useState(false);

  const shuffleArray = <T,>(arr: T[]): T[] => {
    const a = arr.slice();
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  };

  const ensureThread = async () => {
    if (!threadId.current) {
      const t = await openai.beta.threads.create();
      threadId.current = t.id;
    }
    return threadId.current!;
  };

  const ask = async (prompt: string): Promise<string> => {
    setLoading(true);
    try {
      const tid = await ensureThread();
      await openai.beta.threads.messages.create(tid, {
        role: "user",
        content: prompt,
      });
      const run = await openai.beta.threads.runs.createAndPoll(tid, {
        assistant_id: assistantId,
      });
      const msgs = await openai.beta.threads.messages.list(tid);
      const msg = msgs.data.find((m) => m.run_id === run.id && m.role === "assistant");
      const block = msg?.content.find((b) => b.type === "text");
      return block && "text" in block ? block.text.value : "";
    } finally {
      setLoading(false);
    }
  };

  const parseRound = (text: string): RoundData => {
    const lines = text.split("\n").filter((l) => l.trim());
    const theme = lines[0].replace("Thème :", "").trim();
    const aff = lines
      .filter((l) => /^\d+\./.test(l))
      .map((l) => l.replace(/^\d+\.\s*/, "").trim());
    const match = text.match(/Réponse\s*:\s*n°?(\d+)/);
    const idx = match ? parseInt(match[1], 10) - 1 : -1;
    const shuffled = shuffleArray(aff);
    const correctVal = aff[idx];
    const correctIndex = shuffled.indexOf(correctVal);
    return { theme, affirmations: shuffled, correctIndex };
  };

  const handleAddPlayer = () => {
    const name = nameInput.trim();
    if (name && !players.includes(name)) {
      setPlayers([...players, name]);
      setScores((s) => ({ ...s, [name]: 0 }));
      setNameInput("");
    }
  };

  const handleRemovePlayer = (i: number) => {
    const name = players[i];
    setPlayers(players.filter((_, j) => j !== i));
    setScores((s) => {
      const copy = { ...s };
      delete copy[name];
      return copy;
    });
  };

  const startRound = async () => {
    const reply = await ask(`Les joueurs sont : ${players.join(", ")}`);
    const rd = parseRound(reply);
    setRoundData(rd);
    const iv: Record<string, number | null> = {};
    players.forEach((p) => (iv[p] = null));
    setVotes(iv);
    setCurrentReader(0);
    setStep("reading");
  };

  const nextReader = () => {
    if (currentReader < players.length - 1) {
      setCurrentReader((i) => i + 1);
    } else {
      setStep("voting");
    }
  };

  const castVote = (p: string, idx: number) => {
    setVotes((v) => ({ ...v, [p]: idx }));
  };

  const finishVoting = () => {
    const counts: Record<number, number> = {};
    let total = 0;
    players.forEach((p) => {
      const v = votes[p];
      if (v != null) {
        total++;
        counts[v] = (counts[v] || 0) + 1;
      }
    });
    const ks = Object.keys(counts).map((k) => parseInt(k, 10));
    const unanimous = ks.length === 1 && counts[ks[0]] === total && total > 0;
    const newScores = { ...scores };
    if (players.length === 3) {
      players.forEach((p) => {
        if (votes[p] === roundData!.correctIndex) {
          newScores[p] = (newScores[p] || 0) + 1;
        }
      });
    } else {
      if (!unanimous) {
        ks.forEach((k) => {
          if (counts[k] > 0) {
            const author = players[k];
            newScores[author] = (newScores[author] || 0) + 1;
          }
        });
      }
    }
    setScores(newScores);
    setStep("reveal");
  };

  const nextRound = async () => {
    if (round < 15) {
      setRound((r) => r + 1);
      const reply = await ask("prochain thème");
      const rd = parseRound(reply);
      setRoundData(rd);
      const iv: Record<string, number | null> = {};
      players.forEach((p) => (iv[p] = null));
      setVotes(iv);
      setCurrentReader(0);
      setStep("reading");
    } else {
      setStep("end");
    }
  };

  const podium = Object.entries(scores)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3);

  return (
    <div className="container" style={{ position: "relative" }}>
      <button
        onClick={onQuit}
        disabled={loading}
        style={{
          position: "absolute",
          top: 12,
          right: 12,
          background: "transparent",
          border: "none",
          fontSize: 20,
          cursor: "pointer",
          color: "white",
        }}
        title="Quitter"
      >
        ❌
      </button>

      {loading && <p className="loading">💬 GPT réfléchit…</p>}

      {step === "rules" && (
        <GameRulesCard
          logo={menteurLogo}
          gameName="Menteur Menteur"
          rules={[
            "▸Chaque manche, GPT propose un thème et plusieurs affirmations (1 vraie, les autres fausses).",
            "▸Les joueurs lisent chacun à leur tour leur affirmation.",
            "▸Tout le monde vote pour l'affirmation qu'il pense vraie (self-vote autorisé).",
            "▸3 joueurs : chaque votant correct gagne 1 pt.",
            "▸≥ 4 joueurs : l’auteur d’une affirmation avec ≥ 1 vote gagne 1 pt (sauf unanimité).",
            "▸Unanimité ⇒ 0 pt pour l’auteur (trop évident).",
          ]}
          onNext={() => setStep("register")}
          loading={loading}
        />
      )}

      {step === "register" && (
        <>
          <h3>📝 Inscris les joueurs</h3>
          <div style={{ display: "flex", gap: 8 }}>
            <input
              className="fullWidth"
              placeholder="Prénom"
              value={nameInput}
              onChange={(e) => setNameInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleAddPlayer()}
            />
            <button onClick={handleAddPlayer}>＋</button>
          </div>
          <ul>
            {players.map((p, i) => (
              <li key={p}>
                {p} <button onClick={() => handleRemovePlayer(i)}>❌</button>
              </li>
            ))}
          </ul>
          {players.length >= 3 && (
            <button className="fullWidth" onClick={startRound} disabled={loading}>
              🎲 Commencer la partie
            </button>
          )}
        </>
      )}

      {step === "reading" && roundData && (
        <>
          <h3>Manche {round}</h3>
          <h4>Thème : {roundData.theme}</h4>
          <p>
            <strong>{players[currentReader]}</strong>, lis ton affirmation :
          </p>
          <blockquote style={{ fontStyle: "italic" }}>
            {roundData.affirmations[currentReader]}
          </blockquote>
          <button onClick={nextReader}>
            {currentReader < players.length - 1
              ? "Joueur suivant"
              : "Passer au vote"}
          </button>
        </>
      )}

      {step === "voting" && roundData && (
        <>
          <h3>Désignez l’affirmation correcte</h3>
          <ul>
            {players.map((p, i) => (
              <li key={p}>
                <strong>{p}</strong> : {roundData.affirmations[i]}
              </li>
            ))}
          </ul>
          {players.map((p) => (
            <div key={p}>
              {p} vote :
              <select
                value={votes[p] ?? ""}
                onChange={(e) => castVote(p, parseInt(e.target.value))}
              >
                <option value="">Choisir…</option>
                {players.map((p2, j) => (
                  <option key={p2} value={j}>
                    {p2}
                  </option>
                ))}
              </select>
            </div>
          ))}
          <button className="fullWidth" onClick={finishVoting} disabled={loading}>
            Afficher la réponse
          </button>
        </>
      )}

      {step === "reveal" && roundData && (
        <>
          <h3>
            ✅ Réponse :{" "}
            <span style={{ color: "goldenrod" }}>
              {players[roundData.correctIndex]}
            </span>
          </h3>
          <ul>
            {players.map((p, i) => (
              <li key={p}>
                <span
                  style={{
                    color: i === roundData.correctIndex ? "goldenrod" : undefined,
                  }}
                >
                  {p}: {roundData.affirmations[i]}
                </span>{" "}
                — {scores[p] || 0} pts
              </li>
            ))}
          </ul>
          <button className="fullWidth" onClick={nextRound} disabled={loading}>
            Manche suivante
          </button>
        </>
      )}

      {step === "end" && (
        <>
          <h2>🏆 Fin de partie !</h2>
          <ol>
            {podium.map(([p, pts], idx) => (
              <li key={p}>
                {idx === 0 ? "🥇" : idx === 1 ? "🥈" : "🥉"} {p} — {pts} pts
              </li>
            ))}
          </ol>
          <button className="fullWidth" onClick={onQuit}>
            Retour au menu
          </button>
        </>
      )}
    </div>
  );
}
